import {
  CURRENT_PROGRESS_SCHEMA_VERSION,
  createProgress,
  type Progress,
} from "../../domain/progress/model/progress";
import type { ProgressRepository } from "../../domain/progress/progress-repository";
import {
  type PersistedProgress,
  persistedProgressSchema,
  schemaVersionProbeSchema,
} from "./progress-schema";

/** `Progress` の保存先キー（`spec/design.md` 「永続化とデータモデル」節と一致させること）。 */
const PROGRESS_KEY = "vimquest.progress.v1";

/** 破損データの退避先。最新1件のみを保持する（容量を圧迫しない）。 */
const CORRUPT_KEY = "vimquest.progress.v1.corrupt";

/**
 * `schemaVersion` が現行より古い場合のマイグレーション拡張点。
 * 現状は v1 未満の保存データが存在しないため、常に「復元不能」として `null` を返す。
 * 将来 v2 以降を導入した際、ここに変換ロジックを追加する。
 */
function migrateToLatest(_data: unknown, _fromVersion: number): unknown | null {
  return null;
}

/**
 * `Progress`（`ReadonlySet`）と `PersistedProgress`（配列）の相互変換。
 * 配列化の順序はテストで比較しやすいよう決定的にする（ソート済み）。
 */
function toPersisted(progress: Progress): PersistedProgress {
  return {
    schemaVersion: CURRENT_PROGRESS_SCHEMA_VERSION,
    clearedDrills: Array.from(progress.clearedDrills).sort(),
    stamps: Array.from(progress.stamps).sort(),
    updatedAt: progress.updatedAt,
  };
}

function toDomain(data: PersistedProgress): Progress {
  return {
    schemaVersion: data.schemaVersion,
    clearedDrills: new Set(data.clearedDrills),
    stamps: new Set(data.stamps),
    updatedAt: data.updatedAt,
  };
}

/**
 * localStorageを保存先とする `ProgressRepository` の実装（F-008 第一の保存先）。
 *
 * 読み込み手順は `spec/design.md` の読み込みフローチャート（NFR-009）に厳密に従う。
 * 壊れたデータ・未来のバージョン・Zod検証失敗のいずれも例外を投げず、
 * 破損データを `.corrupt` キーへ退避したうえで初期 `Progress` を返す
 * （呼び出し元をクラッシュさせない）。
 */
export class LocalStorageProgressRepository implements ProgressRepository {
  constructor(private readonly storage: Storage = window.localStorage) {}

  /**
   * 直前の`load()`が破損データ等からの復旧だったか（E-6検知用）。データが単に
   * 存在しない（初回訪問）場合は復旧ではないため`false`のままにする。
   */
  private recoveryOccurred = false;

  load(): Progress {
    this.recoveryOccurred = false;
    const raw = this.storage.getItem(PROGRESS_KEY);
    if (raw === null) {
      return createProgress();
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      this.backupCorrupt(raw);
      this.recoveryOccurred = true;
      return createProgress();
    }

    const probe = schemaVersionProbeSchema.safeParse(parsed);
    if (!probe.success || probe.data.schemaVersion > CURRENT_PROGRESS_SCHEMA_VERSION) {
      this.backupCorrupt(raw);
      this.recoveryOccurred = true;
      return createProgress();
    }

    let candidate: unknown = parsed;
    if (probe.data.schemaVersion < CURRENT_PROGRESS_SCHEMA_VERSION) {
      const migrated = migrateToLatest(parsed, probe.data.schemaVersion);
      if (migrated === null) {
        this.backupCorrupt(raw);
        this.recoveryOccurred = true;
        return createProgress();
      }
      candidate = migrated;
    }

    const result = persistedProgressSchema.safeParse(candidate);
    if (!result.success) {
      this.backupCorrupt(raw);
      this.recoveryOccurred = true;
      return createProgress();
    }

    return toDomain(result.data);
  }

  hadRecoveryError(): boolean {
    return this.recoveryOccurred;
  }

  save(progress: Progress): void {
    const persisted = toPersisted(progress);
    try {
      this.storage.setItem(PROGRESS_KEY, JSON.stringify(persisted));
    } catch {
      // 容量超過等でsetItemが例外を投げても、学習者の操作を止めない
      // （エラーハンドリング方針: 保存はベストエフォート。呼び出し元へは再throwしない）。
    }
  }

  clear(): void {
    // corruptキーは診断用に残し、ここでは触らない。
    this.storage.removeItem(PROGRESS_KEY);
  }

  /** 破損データの退避（最新1件のみ）。退避処理自体の失敗も `load()` を落とさない。 */
  private backupCorrupt(rawValue: string): void {
    try {
      this.storage.setItem(CORRUPT_KEY, rawValue);
    } catch {
      // ベストエフォート。退避に失敗しても初期Progressでの起動は継続する。
    }
  }
}
